import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type {
  ChangeApprovalFlowConfig,
  ChangeApprovalFlowConfigManager,
  ChangeWorkGroup,
} from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { memberLabel, membersForGroup } from '../emergencyPolicyModel';
import ChangeGroupMemberSelects from './ChangeGroupMemberSelects';
import SearchableSelect from './SearchableSelect';

const NO_TYPE = '__sem_tipo__';

interface Props {
  config: ChangeApprovalFlowConfig;
  areas: ChangeWorkGroup[];
  typeOptions: { code: string; name: string }[];
  canManage: boolean;
  isNew: boolean;
  onBack: () => void;
  onSaved: () => void;
}

const ChangeApprovalFlowEditor: React.FC<Props> = ({
  config: initial,
  areas,
  typeOptions,
  canManage,
  isNew,
  onBack,
  onSaved,
}) => {
  const { showConfirm } = useModal();
  const [draft, setDraft] = useState<ChangeApprovalFlowConfig>(initial);
  const [newGroupId, setNewGroupId] = useState('');
  const [newUserId, setNewUserId] = useState(0);
  const [immGroupId, setImmGroupId] = useState('');
  const [immUserId, setImmUserId] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(initial);
    setNewGroupId('');
    setNewUserId(0);
    setImmGroupId('');
    setImmUserId(0);
    setError(null);
  }, [initial, areas]);

  const typeSelectOptions = useMemo(
    () => [
      ...typeOptions.map((item) => ({ value: item.code, label: item.name })),
      { value: NO_TYPE, label: 'Sem tipo (fallback)' },
    ],
    [typeOptions],
  );

  const typeValue = draft.change_type_code ? draft.change_type_code : NO_TYPE;

  const managersByGroup = useMemo(() => {
    const map = new Map<number, ChangeApprovalFlowConfigManager[]>();
    draft.managers.forEach((item) => {
      const list = map.get(item.group_id) || [];
      list.push(item);
      map.set(item.group_id, list);
    });
    return map;
  }, [draft.managers]);

  const sortedSteps = useMemo(
    () =>
      [...draft.steps].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.group_id - b.group_id,
      ),
    [draft.steps],
  );

  const stepGroupIds = useMemo(
    () => new Set(draft.steps.map((s) => s.group_id)),
    [draft.steps],
  );

  /** Grupos que já têm linha em Transporte (alias explícito para filtros dos selects). */
  const groupsAlreadyUsed = stepGroupIds;

  /** Gestor vinculado ao grupo do solicitante (1º passo), sem entrar na lista de Transporte. */
  const immediateManagerRows = useMemo(
    () => draft.managers.filter((m) => !stepGroupIds.has(m.group_id)),
    [draft.managers, stepGroupIds],
  );

  const immediateGroupIds = useMemo(
    () => new Set(immediateManagerRows.map((m) => m.group_id)),
    [immediateManagerRows],
  );

  const managerUserIdForGroup = (groupId: number) => {
    const list = managersByGroup.get(groupId) || [];
    return list[0]?.user_id || 0;
  };

  const bindManager = useCallback(
    (groupId: number, userId: number, includeTransportStep: boolean) => {
      const area = areas.find((item) => item.group_id === groupId);
      if (!groupId || !area || !userId) return;
      const member = membersForGroup(areas, groupId).find((m) => m.user_id === userId);
      setError(null);
      setDraft((prev) => {
        const hasStep = prev.steps.some((s) => s.group_id === groupId);
        const nextSteps =
          includeTransportStep && !hasStep
            ? [
                ...prev.steps,
                {
                  group_id: groupId,
                  name: area.name,
                  sort_order: prev.steps.length + 1,
                  is_active: true,
                },
              ]
            : prev.steps;
        return {
          ...prev,
          steps: nextSteps,
          managers: [
            ...prev.managers.filter((m) => m.group_id !== groupId),
            {
              group_id: groupId,
              group_name: area.name,
              user_id: userId,
              user_name: member?.name,
              user_email: member?.email,
            },
          ],
        };
      });
    },
    [areas],
  );

  const addApprovalRow = useCallback(
    (groupId: number, userId: number) => bindManager(groupId, userId, true),
    [bindManager],
  );

  const addImmediateManagerRow = useCallback(
    (groupId: number, userId: number) => bindManager(groupId, userId, false),
    [bindManager],
  );

  const setManagerForGroup = (groupId: number, userId: number) => {
    if (!groupId || !userId) return;
    const member = membersForGroup(areas, groupId).find((m) => m.user_id === userId);
    const area = areas.find((a) => a.group_id === groupId);
    setDraft((prev) => ({
      ...prev,
      managers: [
        ...prev.managers.filter((m) => m.group_id !== groupId),
        {
          group_id: groupId,
          group_name: area?.name,
          user_id: userId,
          user_name: member?.name,
          user_email: member?.email,
        },
      ],
    }));
  };

  const onPickTransportRow = (groupId: number, userId: number) => {
    addApprovalRow(groupId, userId);
    setNewGroupId('');
    setNewUserId(0);
  };

  const onPickImmediateRow = (groupId: number, userId: number) => {
    addImmediateManagerRow(groupId, userId);
    setImmGroupId('');
    setImmUserId(0);
  };

  const removeTransportRow = (groupId: number) => {
    setDraft((prev) => ({
      ...prev,
      steps: prev.steps.filter((s) => s.group_id !== groupId),
      managers: prev.managers.filter((m) => m.group_id !== groupId),
    }));
  };

  const removeImmediateRow = (groupId: number) => {
    setDraft((prev) => ({
      ...prev,
      managers: prev.managers.filter((m) => m.group_id !== groupId),
    }));
  };

  const save = async () => {
    if (!draft.name.trim()) {
      setError('Informe o nome da aprovação.');
      return;
    }
    const configuredRows = sortedSteps.filter((s) => managerUserIdForGroup(s.group_id) > 0);
    if (draft.is_active && configuredRows.length === 0 && !draft.use_immediate_manager) {
      setError('Adicione ao menos um grupo com gestor ou ative o gerente imediato do solicitante.');
      return;
    }
    const label = draft.name.trim();
    const ok = await showConfirm({
      title: isNew ? 'Criar aprovação' : 'Salvar aprovação',
      message: isNew
        ? `Deseja criar a aprovação «${label}»?`
        : `Tem certeza que deseja salvar as alterações da aprovação «${label}»?`,
      confirmLabel: isNew ? 'Criar' : 'Salvar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setSaving(true);
    setError(null);
    const payload = {
      name: draft.name.trim(),
      change_type_code: draft.change_type_code || null,
      is_active: draft.is_active,
      use_immediate_manager: draft.use_immediate_manager,
      parallel_after_first: draft.parallel_after_first,
      steps: sortedSteps.map((item, index) => ({
        group_id: item.group_id,
        sort_order: index + 1,
        is_active: item.is_active,
      })),
      managers: draft.managers
        .filter((item) => stepGroupIds.has(item.group_id))
        .map((item) => ({
          group_id: item.group_id,
          user_id: item.user_id,
        })),
    };
    try {
      if (isNew) {
        await changesApi.createApprovalFlowConfig(payload);
      } else {
        await changesApi.replaceApprovalFlowConfig(draft.id, payload);
      }
      onSaved();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Button variant="link" className="ps-0 mb-2" onClick={onBack}>
        ← Voltar para a lista
      </Button>
      <Card className="border-primary">
        <Card.Header className="fw-semibold">
          {isNew ? 'Nova aprovação' : `Editar aprovação: ${draft.name}`}
        </Card.Header>
        <Card.Body>
          {error ? (
            <Alert variant="danger" className="py-2">
              {error}
            </Alert>
          ) : null}

          <Row className="g-2 mb-3">
            <Col md={6}>
              <Form.Label>Nome</Form.Label>
              <Form.Control
                value={draft.name}
                disabled={!canManage}
                onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Tipo de mudança</Form.Label>
              <SearchableSelect
                ariaLabel="Tipo de mudança"
                value={typeValue}
                searchEnabled
                disabled={!canManage}
                options={typeSelectOptions}
                onChange={(value) =>
                  setDraft((p) => ({
                    ...p,
                    change_type_code: String(value) === NO_TYPE ? null : String(value),
                  }))
                }
              />
            </Col>
          </Row>

          <Form.Check
            type="switch"
            className="mb-2"
            disabled={!canManage}
            label="Regra ativa para este tipo (fluxo fixo em Transporte / gestores)"
            checked={draft.is_active}
            onChange={(e) => setDraft((p) => ({ ...p, is_active: e.target.checked }))}
          />
          <p className="text-muted small mb-3">
            Desligada: não aplica esta regra. Ligada: usa os grupos e gestores da lista abaixo (quantos
            precisar).
          </p>

          <Form.Check
            type="switch"
            className="mb-2"
            disabled={!canManage || !draft.is_active}
            label="Gerente imediato do solicitante (1º passo — usa o solicitante da mudança, não quem está logado)"
            checked={draft.use_immediate_manager}
            onChange={(e) => setDraft((p) => ({ ...p, use_immediate_manager: e.target.checked }))}
          />
          {draft.use_immediate_manager && draft.is_active ? (
            <Alert variant="info" className="py-2 small mb-3">
              O <strong>1º passo</strong> é resolvido por mudança: grupo do solicitante (ou escolha na
              criação se ele estiver em vários grupos), gestor com flag em{' '}
              <strong>Cadastros → Grupos</strong> e, opcionalmente, gerente imediato individual por
              membro. Não vincule aqui uma pessoa fixa para todos os usuários.
            </Alert>
          ) : null}

          <div className={!draft.is_active ? 'opacity-50 pe-none' : undefined}>
            <Form.Check
              type="radio"
              name={`parallel-${draft.id}`}
              className="mb-1"
              disabled={!canManage || !draft.is_active}
              label="Demais aprovações em paralelo"
              checked={draft.parallel_after_first}
              onChange={() => setDraft((p) => ({ ...p, parallel_after_first: true }))}
            />
            <Form.Check
              type="radio"
              name={`parallel-${draft.id}`}
              className="mb-3"
              disabled={!canManage || !draft.is_active}
              label="Demais aprovações em sequência"
              checked={!draft.parallel_after_first}
              onChange={() => setDraft((p) => ({ ...p, parallel_after_first: false }))}
            />

            <p className="fw-semibold small mb-2">Demais aprovações (Transporte)</p>
            <p className="text-muted small">
              Escolha grupo e gestor — cada par vira uma linha. Repita quantas vezes precisar (4, 5…).
            </p>

            {canManage ? (
              <div className="mb-3">
                <ChangeGroupMemberSelects
                  areas={areas}
                  groupId={newGroupId}
                  userId={newUserId}
                  disabled={!canManage || !draft.is_active}
                  filterGroups={(a) => !groupsAlreadyUsed.has(a.group_id)}
                  onGroupChange={(value) => {
                    setNewGroupId(value);
                    setNewUserId(0);
                  }}
                  onUserChange={setNewUserId}
                  onPickComplete={onPickTransportRow}
                />
              </div>
            ) : null}

            <Table responsive size="sm" className="mb-3 align-middle">
              <thead>
                <tr>
                  {!draft.parallel_after_first ? <th>#</th> : null}
                  <th>Grupo / área</th>
                  <th>Gestor aprovador</th>
                  {canManage ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {sortedSteps.length === 0 ? (
                  <tr>
                    <td
                      colSpan={(draft.parallel_after_first ? 2 : 3) + (canManage ? 1 : 0)}
                      className="text-muted"
                    >
                      Nenhuma aprovação de Transporte ainda. Selecione grupo e gestor acima.
                    </td>
                  </tr>
                ) : (
                  sortedSteps.map((step, index) => {
                    const area = areas.find((a) => a.group_id === step.group_id);
                    const members = membersForGroup(areas, step.group_id);
                    const selectedId = managerUserIdForGroup(step.group_id);
                    const displayIndex = draft.use_immediate_manager
                      ? index + 2
                      : index + 1;
                    return (
                      <tr key={step.group_id}>
                        {!draft.parallel_after_first ? <td>{displayIndex}</td> : null}
                        <td>{step.name || area?.name || step.group_id}</td>
                        <td>
                          {canManage ? (
                            <Form.Select
                              size="sm"
                              aria-label={`Gestor para ${step.name || step.group_id}`}
                              value={selectedId || ''}
                              disabled={!draft.is_active}
                              onChange={(e) =>
                                setManagerForGroup(step.group_id, Number(e.target.value))
                              }
                            >
                              <option value="">
                                {members.length ? 'Selecione o gestor' : 'Sem membros no grupo'}
                              </option>
                              {members.map((member) => (
                                <option key={member.user_id} value={member.user_id}>
                                  {memberLabel(member)}
                                  {member.is_manager ? ' · Gestor no grupo' : ''}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            (managersByGroup.get(step.group_id) || []).map((m) => (
                              <Badge key={`${m.group_id}-${m.user_id}`} bg="primary" className="me-1">
                                {m.user_email || m.user_name || `#${m.user_id}`}
                              </Badge>
                            ))
                          )}
                        </td>
                        {canManage ? (
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={!draft.is_active}
                              onClick={() => removeTransportRow(step.group_id)}
                            >
                              Remover
                            </Button>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex gap-2 mt-4">
            {canManage ? (
              <Button onClick={() => void save()} disabled={saving}>
                {saving ? 'Salvando…' : 'Salvar'}
              </Button>
            ) : null}
            <Button variant="outline-secondary" onClick={onBack}>
              Voltar
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ChangeApprovalFlowEditor;
